import { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import DownloadIcon from '@mui/icons-material/Download';

const VisitDailyTab = ({ visitDaily = {} }) => {
    const { t: translate } = useTranslation();

    // Get sorted dates and metrics
    const { dates, metrics } = useMemo(() => {
        const sortedDates = Object.keys(visitDaily).sort();
        const metricKeys = ['totalVisits', 'totalUniqueVisits', 'avgPagesPerVisitor', 'totalBookingsMade', 'totalMoneyMade'];
        return { dates: sortedDates, metrics: metricKeys };
    }, [visitDaily]);

    const hasData = dates.length > 0;

    // Transpose data: metrics as rows, dates as columns
    const transposedData = useMemo(() => {
        if (!hasData) return [];

        return metrics.map((metric) => {
            const row = { metric };
            dates.forEach((date) => {
                row[date] = visitDaily[date]?.[metric] ?? null;
            });
            return row;
        });
    }, [visitDaily, dates, metrics, hasData]);

    // Dynamic column definitions: first column is "Metric", then one column per date
    const columnDefinitions = useMemo(() => {
        const cols = [{ key: 'metric', label: 'Metric', width: 180 }];
        dates.forEach((date) => {
            cols.push({
                key: date,
                label: date,
                width: 120,
            });
        });
        return cols;
    }, [dates]);

    // State for column widths (resizable columns)
    const [columns, setColumns] = useState(
        columnDefinitions.map((col) => ({
            columnId: col.key,
            width: col.width,
            resizable: true,
        }))
    );

    // Handle column resize
    const handleColumnResize = useCallback((columnId, width) => {
        setColumns((prevColumns) => prevColumns.map((col) => (col.columnId === columnId ? { ...col, width } : col)));
    }, []);

    // CSV Export function - matches reporting service format
    const exportToCSV = useCallback(() => {
        if (!hasData) return;

        // Format dates as dd/mm/yyyy
        const formattedDates = dates.map((date) => {
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year}`;
        });

        // Build header row: "Metric" followed by formatted dates
        const headerRow = ['Metric', ...formattedDates].join(',');

        // Build metric rows
        const metricLabels = {
            totalVisits: 'Total Visits',
            totalUniqueVisits: 'Total Unique Visits',
            avgPagesPerVisitor: 'Avg Pages Per Visitor',
            totalBookingsMade: 'Total Bookings Made',
            totalMoneyMade: 'Total Money Made',
        };

        const metricRows = metrics.map((metric) => {
            const values = dates.map((date) => {
                const value = visitDaily[date]?.[metric];
                if (value === null || value === undefined) return '';

                // Format based on metric type
                if (metric === 'totalMoneyMade') {
                    return typeof value === 'number' ? value.toFixed(2) : String(value);
                } else if (metric === 'avgPagesPerVisitor') {
                    return typeof value === 'number' ? value.toFixed(2) : String(value);
                }
                return String(value);
            });
            return [metricLabels[metric] || metric, ...values].join(',');
        });

        // Add average conversion row
        const conversionValues = dates.map((date) => {
            const dateData = visitDaily[date];
            if (!dateData) return '';

            const bookings = Number(dateData.totalBookingsMade) || 0;
            const uniqueVisits = Number(dateData.totalUniqueVisits) || 0;

            if (uniqueVisits > 0) {
                const conversionRate = (bookings / uniqueVisits) * 100;
                return conversionRate.toFixed(2);
            }
            return '0.00';
        });
        const conversionRow = ['Avg Conversion (%)', ...conversionValues].join(',');

        const csvContent = [headerRow, ...metricRows, conversionRow].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `traffic-visit-daily-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [hasData, dates, metrics, visitDaily]);

    const headerRow = useMemo(() => {
        return {
            rowId: 'header',
            cells: columnDefinitions.map((col) => {
                // Format date headers as dd/mm/yyyy
                if (col.key !== 'metric') {
                    const [year, month, day] = col.key.split('-');
                    const formattedDate = `${day}/${month}/${year}`;
                    return {
                        type: 'header',
                        text: formattedDate,
                    };
                }
                return {
                    type: 'header',
                    text: col.label,
                };
            }),
        };
    }, [columnDefinitions]);

    const rows = useMemo(() => {
        if (!hasData) return [headerRow];

        const metricLabels = {
            totalVisits: translate('traffic.visitDaily.totalVisits') || 'Total Visits',
            totalUniqueVisits: translate('traffic.visitDaily.totalUniqueVisits') || 'Total Unique Visits',
            avgPagesPerVisitor: translate('traffic.visitDaily.avgPagesPerVisitor') || 'Avg Pages/Visitor',
            totalBookingsMade: translate('traffic.visitDaily.totalBookingsMade') || 'Bookings Made',
            totalMoneyMade: translate('traffic.visitDaily.totalMoneyMade') || 'Money Made',
        };

        const dataRows = transposedData.map((row, idx) => {
            return {
                rowId: idx,
                cells: columnDefinitions.map((col) => {
                    const value = row[col.key];

                    // First column: metric name
                    if (col.key === 'metric') {
                        return {
                            type: 'text',
                            text: metricLabels[value] || String(value),
                        };
                    }

                    // Data columns: format based on metric
                    let displayValue = value ?? '';

                    if (value !== null && value !== undefined) {
                        if (row.metric === 'totalMoneyMade' && typeof value === 'number') {
                            displayValue = `$${value.toFixed(2)}`;
                        } else if (row.metric === 'avgPagesPerVisitor' && typeof value === 'number') {
                            displayValue = value.toFixed(2);
                        } else {
                            displayValue = String(value);
                        }
                    }

                    return {
                        type: 'text',
                        text: String(displayValue),
                    };
                }),
            };
        });

        // Add average conversion row: (totalBookingsMade / totalUniqueVisits) * 100
        const avgConversionRow = {
            rowId: 'avgConversion',
            cells: columnDefinitions.map((col) => {
                // First column: metric name
                if (col.key === 'metric') {
                    return {
                        type: 'text',
                        text: translate('traffic.visitDaily.avgConversion') || 'Avg Conversion (%)',
                    };
                }

                // Data columns: calculate conversion rate for each date
                const dateData = visitDaily[col.key];
                let displayValue = '';

                if (dateData) {
                    const bookings = Number(dateData.totalBookingsMade) || 0;
                    const uniqueVisits = Number(dateData.totalUniqueVisits) || 0;

                    if (uniqueVisits > 0) {
                        const conversionRate = (bookings / uniqueVisits) * 100;
                        displayValue = `${conversionRate.toFixed(2)}%`;
                    } else {
                        displayValue = '0.00%';
                    }
                }

                return {
                    type: 'text',
                    text: String(displayValue),
                };
            }),
        };

        return [headerRow, ...dataRows, avgConversionRow];
    }, [hasData, transposedData, headerRow, columnDefinitions, translate, visitDaily]);

    if (!hasData) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: '240px',
                    border: '1px dashed #d1d1d1',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    gap: 1,
                }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#6f6f6f' }}>
                    {translate('traffic.visitDaily_tab') || 'Daily Visits'}
                </Typography>
                <Typography sx={{ color: '#9a9a9a' }}>{translate('traffic.visitDaily_empty') || 'No daily visits data available'}</Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                    {translate('traffic.visitDaily_tab') || 'Daily Visits'} ({dates.length} {dates.length === 1 ? 'day' : 'days'})
                </Typography>
                <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={exportToCSV} disabled={!hasData}>
                    {translate('traffic.export_csv') || 'Export to CSV'}
                </Button>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    height: 'calc(100vh - 300px)',
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    '& .rg-root': {
                        '--rg-cell-border-color': '#e0e0e0',
                        '--rg-header-background-color': '#f5f5f5',
                        '--rg-selection-border-color': '#1976d2',
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    },
                }}>
                <ReactGrid
                    rows={rows}
                    columns={columns}
                    onColumnResized={handleColumnResize}
                    enableRangeSelection
                    enableRowSelection
                    enableColumnSelection
                    stickyTopRows={1}
                />
            </Box>
        </Box>
    );
};

VisitDailyTab.displayName = 'VisitDailyTab';

export default VisitDailyTab;
